<template>
  <div ref="resultsPage" class="container">
    <div class="header">
      <h1>Here's what we learned:</h1>
      <img v-if="whitelabelUseResultsPageImages" src="@/assets/images/agents.png" alt="Make It Cheaper agents photo">
    </div>

    <div class="score-result">
      <img v-if="whitelabelUseResultsPageImages" class="icon" :src="summaryIcon" alt="Resulting usage icon">
      <div class="score-summary">
        <p class="text" v-html="summaryMessage" /> <!-- eslint-disable-line vue/no-v-html -->

        <div v-if="isBroadband" class="score-bar">
          <div class="score" :class="scoreBarScore" />
          <strong class="text">Usage</strong>
        </div>
      </div>
    </div>

    <ul v-if="isBroadband" :style="checkboxBackground" class="requirements">
      <!-- eslint-disable vue/no-v-html -->
      <li v-html="broadbandRequirements" />
      <li v-if="isPhone" v-html="phoneRequirements" />
      <!-- eslint-enable vue/no-v-html -->
    </ul>

    <div :class="{ footer: contentShorterThanViewport }">
      <hr class="rule">
      <div class="conclusion">
        <img v-if="whitelabelUseResultsPageImages" class="icon" src="@/assets/icons/right-product.svg" alt="Finding the right product illustration">
        <p class="text">
          <b>Let's connect with our telecoms experts and find the right product for you</b>
        </p>
      </div>

      <router-link to="/chat" class="btn-wrap">
        <MicButton class="chat-button" type="button" :shadow="true" :has-arrow="!whitelabelButtonIcon">
          Great, let's talk
          <img
            v-if="whitelabelButtonIcon"
            :src="require(`@mic/frontend/assets/icons/${whitelabelButtonIcon}`)"
            alt="button icon"
            class="next-btn__icon"
          >
        </MicButton>
      </router-link>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import MicButton from '@mic/frontend/buttons/MicButton'
import phoneIcon from '@/assets/icons/phone-result.svg'
import broadbandIconLow from '@/assets/icons/broadband-result-low.svg'
import broadbandIconMedium from '@/assets/icons/broadband-result-medium.svg'
import broadbandIconHigh from '@/assets/icons/broadband-result-high.svg'

const whitelabel = require('../../whitelabel.config')

export default {
  name: 'ResultsPage',

  components: {
    MicButton
  },

  data () {
    return {
      scoreThreshold: {
        low: 0,
        medium: 12,
        high: 100
      },
      messages: {
        broadband: {
          low: {
            summary: '<strong>Low internet usage,</strong> requiring a reliable connection and standard speeds',
            requirement: 'You could manage with a <strong>standard broadband</strong> connection'
          },
          medium: {
            summary: '<strong>Medium internet usage,</strong> requiring a reliable connection and fast speeds',
            requirement: 'You would benefit from a <strong>fast fibre</strong> connection'
          },
          high: {
            summary: '<strong>High internet usage,</strong> requiring a strong connection and fast speeds',
            requirement: 'You would benefit from a <strong>superfast fibre</strong> connection'
          }
        },
        phone: {
          low: {
            summary: "<strong>Basic landline needs.</strong> We'll make sure you're not overpaying for your line.",
            requirement: "<strong class='blockable'>Basic landline needs.</strong> We'll make sure you're not overpaying for your line."
          },
          medium: {
            summary: "<strong>Standard landline needs.</strong> We'll talk you through the available call features that suit your needs.",
            requirement: "<strong class='blockable'>Standard landline needs.</strong> We'll find you a package with the right combination of calls and features."
          },
          high: {
            summary: "<strong>Advanced landline needs.</strong> We'll talk you through the available call features that suit your needs.",
            requirement: "<strong class='blockable'>Advanced landline needs.</strong> We'll find you a package with the right combination of calls and features."
          }
        }
      },
      broadbandIcons: {
        low: broadbandIconLow,
        medium: broadbandIconMedium,
        high: broadbandIconHigh
      },
      whitelabelUseResultsPageImages: whitelabel[process.env.VUE_APP_BRAND].useResultsPageImages,
      whitelabelButtonIcon: whitelabel[process.env.VUE_APP_BRAND].buttonIcon,
      whitelabelResultsPageCheckIcon: whitelabel[process.env.VUE_APP_BRAND].resultsPageCheckIcon,
      isMounted: false
    }
  },

  computed: {
    ...mapGetters([
      'isBroadband',
      'isPhone',
      'broadbandScore',
      'phoneScore',
      'getWindowSize'
    ]),

    contentShorterThanViewport () {
      if (this.isMounted) return this.$refs.resultsPage.clientHeight < this.getWindowSize.height
      return false
    },

    summaryMessage () {
      if (this.isBroadband) {
        return this.messages.broadband[this.broadbandScoreBand].summary
      } else {
        return this.messages.phone[this.phoneScore].summary
      }
    },
    broadbandRequirements () {
      return this.messages.broadband[this.broadbandScoreBand].requirement
    },
    phoneRequirements () {
      return this.messages.phone[this.phoneScore].requirement
    },
    broadbandScoreBand () {
      const bands = Object.values(this.scoreThreshold)
      const score = this.broadbandScore

      const selectedIndex = bands.findIndex((band, i) => {
        const nextBand = bands[i + 1]
        const isAboveBand = score >= band
        const isBelowNextBand = score < nextBand

        return isAboveBand && (!nextBand || isBelowNextBand)
      })

      return Object.keys(this.scoreThreshold)[selectedIndex]
    },
    scoreBarScore () {
      return this.broadbandScoreBand
    },
    summaryIcon () {
      return this.isBroadband ? this.broadbandIcons[this.broadbandScoreBand] : phoneIcon
    },
    checkboxBackground () {
      const url = require(`@/assets/icons/${this.whitelabelResultsPageCheckIcon}`)
      return { '--checkbox-background': `url(${url})` }
    }
  },

  mounted () {
    this.isMounted = true
  }
}
</script>

<style lang="scss" scoped>
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: $spacing-12 0;
  min-height: 130px;
  > h1 {
    font-size: $h1-font-size;
    font-weight: bold;
    text-align: left;
  }

  @media (max-width: $screen-sm) {
    min-height: 0;
    margin-top: 16px;
  }

  @media (max-width: $screen-md) {
    h1 {
      font-size: 26px;
      line-height: 32px;
      margin: 0;
    }

    img {
      width: 130px;
    }
  }
}

.score-result {
  display: flex;
  align-items: center;
  min-height: 139px;

  > .icon {
    padding-right: $spacing-52;
  }

  @media (max-width: $screen-md) {
    .icon {
      display: none;
    }
  }
}

.score-summary {
  flex-grow: 1;
  text-align: left;

  > .text {
    font-size: $results-score-summary-font-size;

    @media (max-width: $screen-md) {
      font-size: 20px;
      line-height: 24px;
    }

    .blockable {
      display: block;
    }
  }

  @media (max-width: $screen-md) {
    display: flex;
    flex-direction: column-reverse;
  }
}

.score-bar {
  height: $score-bar-height;
  background-color: $results-score-background;
  position: relative;

  > .text {
    position: absolute;
    top: 0;
    left: 0;
    line-height: $score-bar-height;
    padding-left: $spacing-16;
    color: $white;
  }

  > .score {
    height: $score-bar-height;
    background-color: $voip-primary;

    &.low {
      width: 25%;
    }

    &.medium {
      width: 55%;
    }

    &.high {
      width: 80%;
    }
  }

  @media (max-width: $screen-md) {
    height: 37px;
    margin: $spacing-16 0;

    > .text {
      line-height: 37px;
    }

    > .score {
      height: 37px;
    }
  }
}

.requirements {
  list-style: none;
  text-align: left;
  font-size: $results-score-summary-font-size;
  line-height: $requirements-line-height;
  padding: $spacing-32 0 0;
  margin: 0;

  @media (max-width: $screen-md) {
    font-size: 16px;
    line-height: 19px;
    padding: $spacing-8 0 0;
  }

  > li {
    content: '';
    background-image: var(--checkbox-background);
    background-repeat: no-repeat;
    background-size: $requirements-bullet-size $requirements-bullet-size;
    padding-bottom: $spacing-32;
    padding-left: $requirements-bullet-size + $spacing-20;

    &:last-of-type {
      padding-bottom: 0;
    }
  }
}

.rule {
  height: 3px;
  background-color: $results-rule-background;
  margin: $spacing-32 0;

  @media (max-width: $screen-md) {
    height: 1px;
    margin: $spacing-24 0;

  }

}

.conclusion {
  display: flex;
  justify-content: space-between;
  align-items: center;

  > .icon {
    padding-right: $spacing-32;
  }

  > .text {
    font-size: $results-conclusion-font-size;
    line-height: $conclusion-line-height;
    text-align: left;
    margin: 0;
  }

  @media (max-width: $screen-md) {
    .icon {
      display: none;
    }

    p.text {
      font-size: 20px;
      line-height: 25px;
    }
  }
}

.btn-wrap {
  display: flex;
  justify-content: flex-end;
  margin-bottom: $spacing-32;
  text-decoration: none;

}
.chat-button {
  margin-top: $spacing-32;

  @media (max-width: $screen-md) {
    width: 100%;
  }
}
.next-btn {
  &__icon {
    margin-left: $spacing-8;
  }
}
.footer {
  @media (max-width: $screen-sm) {
    width: calc(100vw - 32px);
    position: fixed;
    bottom: 0;
    left: 16px;
  }
}
</style>
